import { Map } from 'immutable';
import { tFunctionType } from 'app/components/localization';
import {
  DESERIALIZATION_UNIQUE_VIOLATION,
  DESERIALIZATION_VALIDATION_ERROR,
  DESERIALIZATION_ATTRIBUTE_DISPLAY_NAME,
  DESERIALIZATION_ATTRIBUTE_GENDER,
  DESERIALIZATION_ATTRIBUTE_EMAIL,
  DESERIALIZATION_ATTRIBUTE_NAME,
  DESERIALIZATION_ATTRIBUTE_RESET_TOKEN,
  DESERIALIZATION_DETAILS_ERROR_BLANK,
  DESERIALIZATION_DETAILS_ERROR_INVALID_DOMAIN,
  DESERIALIZATION_DETAILS_ERROR_INVALID_EMAIL,
  DESERIALIZATION_DETAILS_ERROR_PASSWORD_RULE,
  DESERIALIZATION_DETAILS_ERROR_REQUIRED,
  DESERIALIZATION_DETAILS_ERROR_REQUIRES_ROMAN,
  DESERIALIZATION_DETAILS_ERROR_TAKEN,
  DESERIALIZATION_DETAILS_ERROR_TOO_LONG,
  RECAPTCHA_FAILED,
  CONSTRAINT_BRAND_UNIQUE_CANONICAL_FALLBACK,
} from 'app/constants/ServerErrors';
import { notify } from 'app/services/errorReporter';
import createErrorType from 'app/utils/createErrorType';
import { errorMessageMatcher, formatGenericError } from 'app/utils/errors';
import ApplicationError from 'app/models/request/ApplicationError';
import { Cause } from 'app/constants/schemas';
import { pipe, constant } from 'fp-ts/lib/function';
import * as iots from 'io-ts';
import { decodeWithDefault } from './ioTs';

export const getAllErrorMessages = (t: tFunctionType, error: unknown) => {
  const errors: string[] = [];
  if (error instanceof ApplicationError && error.causes) {
    error.causes.forEach((e: Cause) => {
      const serverErrorMessage = getServerErrorMessage(t, e);
      if (serverErrorMessage) {
        errors.push(serverErrorMessage);
      } else {
        // Show the server error message to the user and send this error to Honeybadger.
        const errorMessageString = (e && e.detail) ? e.detail : t('auth.error.unexpected');
        errors.push(errorMessageString);
        notify(new UnhandledValidationError(errorMessageString, error));
      }
    });
  } else {
    errors.push(t('auth.error.unexpected'));
  }
  return errors.join('\n');
};

const getMetaAttribute = (error: unknown): string | undefined => {
  if (error instanceof Cause) {
    return pipe(
      error.meta?.get('attribute'),
      (a) => decodeWithDefault(iots.string, a, undefined),
    );
  }
  return undefined;
};

interface IChecks {
  code?: (code: string | null | undefined) => boolean;
  meta?: (meta: Map<string, unknown> | undefined | null) => boolean;
  attribute?: (attr: string) => boolean;
  status?: (status: number | undefined | null) => boolean;
}

const makeCauseCheck = (checks: IChecks) => (error: unknown): boolean => {
  if (!(error instanceof Cause)) {
    return false;
  }

  const attribute = getMetaAttribute(error);
  return Boolean(
    (checks.code === undefined || checks.code(error.code)) &&
    (checks.meta === undefined || checks.meta(error.meta)) &&
    (checks.status === undefined || checks.status(error.status)) &&
    (checks.attribute === undefined || (attribute && checks.attribute(attribute))),
  );
};

const getServerErrorMessage = errorMessageMatcher([
  {
    // The supplied attribute is not unique (e.g. email has been taken)
    check: makeCauseCheck({
      code: (c) => c === DESERIALIZATION_VALIDATION_ERROR,
      meta: (m) => m?.getIn(['details', 'error']) === DESERIALIZATION_DETAILS_ERROR_TAKEN,
      attribute: (a) => [
        DESERIALIZATION_ATTRIBUTE_EMAIL,
      ].includes(a),
    }),
    getMessage: (t: tFunctionType, error: unknown) =>
      t('auth.error.taken', { attribute: translateFieldName(t, getMetaAttribute(error)) }),
  },
  {
    // The text length exceeds the maximum allowed
    check: makeCauseCheck({
      code: (c) => c === DESERIALIZATION_VALIDATION_ERROR,
      meta: (m) => m?.getIn(['details', 'error']) === DESERIALIZATION_DETAILS_ERROR_TOO_LONG,
      attribute: (a) => [
        DESERIALIZATION_ATTRIBUTE_NAME,
        DESERIALIZATION_ATTRIBUTE_DISPLAY_NAME,
        DESERIALIZATION_ATTRIBUTE_GENDER,
      ].includes(a),
    }),
    getMessage: (t: tFunctionType, error: unknown) =>
      t('auth.error.too-long',
        {
          attribute: translateFieldName(t, getMetaAttribute(error)),
          length: error instanceof Cause ? error.meta?.getIn(['details', 'count']) ?? -1 : -1,
        }),
  },
  {
    check: makeCauseCheck({
      code: (c) => c === DESERIALIZATION_UNIQUE_VIOLATION,
      meta: (m) => m?.get('constraint') === CONSTRAINT_BRAND_UNIQUE_CANONICAL_FALLBACK,
    }),
    getMessage: (t: tFunctionType) => t('entity-fields.Brand.canonicalLanguageFallback.unique-violation'),
  },
  {
    // The email is invalid
    check: makeCauseCheck({
      code: (c) => c === DESERIALIZATION_VALIDATION_ERROR,
      meta: (m) => m?.getIn(['details', 'error']) === DESERIALIZATION_DETAILS_ERROR_INVALID_EMAIL,
    }),
    getMessage: (t: tFunctionType) => t('auth.error.email-invalid'),
  },
  {
    // The email comes domain on the deny-list
    check: makeCauseCheck({
      code: (c) => c === DESERIALIZATION_VALIDATION_ERROR,
      meta: (m) => m?.getIn(['details', 'error']) === DESERIALIZATION_DETAILS_ERROR_INVALID_DOMAIN,
    }),
    getMessage: (t: tFunctionType) => t('auth.error.domain-invalid'),
  },
  {
    // The supplied attribute requires roman characters
    check: makeCauseCheck({
      code: (c) => c === DESERIALIZATION_VALIDATION_ERROR,
      meta: (m) => m?.getIn(['details', 'error']) === DESERIALIZATION_DETAILS_ERROR_REQUIRES_ROMAN,
      attribute: (a) => [
        DESERIALIZATION_ATTRIBUTE_DISPLAY_NAME,
        DESERIALIZATION_ATTRIBUTE_GENDER,
      ].includes(a),
    }),
    getMessage: (t: tFunctionType, error: unknown) =>
      t('auth.error.requires-roman', { attribute: translateFieldName(t, getMetaAttribute(error)) }),
  },
  {
    // The required attribute is not present
    check: makeCauseCheck({
      code: (c) => c === DESERIALIZATION_VALIDATION_ERROR,
      meta: (m) => m?.getIn(['details', 'error']) === DESERIALIZATION_DETAILS_ERROR_REQUIRED
        || m?.getIn(['details', 'error']) === DESERIALIZATION_DETAILS_ERROR_BLANK,
      attribute: (a) => [
        DESERIALIZATION_ATTRIBUTE_EMAIL,
        DESERIALIZATION_ATTRIBUTE_NAME,
        DESERIALIZATION_ATTRIBUTE_DISPLAY_NAME,
      ].includes(a),
    }),
    getMessage: (t: tFunctionType, error: unknown) =>
      t('auth.error.required', { attribute: translateFieldName(t, getMetaAttribute(error)) }),
  },
  {
    // The required reset url parameter is not present
    check: makeCauseCheck({
      code: (c) => c === DESERIALIZATION_VALIDATION_ERROR,
      meta: (m) => m?.getIn(['details', 'error']) === DESERIALIZATION_DETAILS_ERROR_BLANK,
      attribute: (a) => a === DESERIALIZATION_ATTRIBUTE_RESET_TOKEN,
    }),
    getMessage: (t: tFunctionType, error: unknown) => t('auth.reset-password-form.messages.try-again'),
  },
  {
    // The password must meet the minimum requirements
    check: makeCauseCheck({
      code: (c) => c === DESERIALIZATION_VALIDATION_ERROR,
      meta: (m) => m?.getIn(['details', 'error']) === DESERIALIZATION_DETAILS_ERROR_PASSWORD_RULE,
    }),
    getMessage: (t: tFunctionType, error: unknown) => t('auth.error.password-invalid'),
  },
  {
    // Recaptcha failed to distinguish user from bot
    check: makeCauseCheck({ code: c => c === RECAPTCHA_FAILED }),
    getMessage: (t: tFunctionType, error: unknown) => t('auth.error.recaptcha'),
  },
  {
    check: makeCauseCheck({ status: (s) => s === 500 }),
    getMessage: (t, error) => formatGenericError(error),
  },
  {
    // Fallback error
    check: constant(true),
    getMessage: constant(null),
  },
]);

function translateFieldName(t: tFunctionType, field: string | undefined) {
  switch (field) {
    case DESERIALIZATION_ATTRIBUTE_NAME:
      return t('auth.error.name-string');
    case DESERIALIZATION_ATTRIBUTE_DISPLAY_NAME:
      return t('auth.error.display-name-string');
    case DESERIALIZATION_ATTRIBUTE_EMAIL:
      return t('auth.error.email-string');
    case DESERIALIZATION_ATTRIBUTE_GENDER:
      return t('auth.error.gender-string');
    default:
      return '';
  }
}

export class UnhandledValidationError extends createErrorType('UnhandledValidation') {
  public constructor(message: string, public readonly cause: unknown) {
    super(message);
  }
}
